import React, { useEffect, useId, useRef, useState } from "react";
import {
  BarChart3,
  Building2,
  ClipboardCheck,
  ClipboardList,
  Files,
  HeartHandshake,
  House,
  Library,
  ListChecks,
  Menu,
  Smartphone,
  Stethoscope,
  TrendingUp,
  Users,
  UsersRound,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  ClientPrimaryNavigationKey,
  ClientSecondaryNavigationKey,
  GlobalPortalNavigationKey,
  SecondaryNavigationItem,
  TeamNavigationKey,
  adminPortalNavigation,
  clientPrimaryNavigation,
  globalPortalNavigation,
  insightsPortalNavigation,
  supervisorPortalNavigation,
  teamNavigation,
} from "../config/navigation";
import { PortalLink } from "./PortalLink";
import {
  ClientWorkspaceContextIndicator,
  useClientWorkspace,
} from "./ClientWorkspace";
import {
  readTeamClinicId,
  withTeamClinicScope,
} from "../utils/teamClinicScope";

interface GlobalPortalNavigationProps {
  activeKey?: GlobalPortalNavigationKey;
  clinicianCode: string;
  therapistRole?: string;
}

const globalNavigationIcons: Record<
  GlobalPortalNavigationKey,
  LucideIcon
> = {
  portal: House,
  team: UsersRound,
  clients: Users,
  templates: Files,
  homework: ClipboardCheck,
  "coping-skills": HeartHandshake,
  "caseload-engagement": BarChart3,
  "homework-overview": ClipboardList,
  "completion-by-device": Smartphone,
  "value-engagement": TrendingUp,
  clinics: Building2,
  therapists: Stethoscope,
  "admin-clients": Users,
  "activity-registry": Library,
  "lists-of-values": ListChecks,
};

function GlobalNavigationIcon({
  navigationKey,
}: {
  navigationKey: GlobalPortalNavigationKey;
}) {
  const Icon = globalNavigationIcons[navigationKey];
  return <Icon aria-hidden="true" focusable="false" />;
}

export function GlobalPortalNavigation({
  activeKey,
  clinicianCode,
  therapistRole,
}: GlobalPortalNavigationProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuId = useId();
  const navigationRef = useRef<HTMLElement>(null);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
  const normalizedRole = therapistRole?.trim().toLowerCase();
  const isAdmin = normalizedRole === "admin";
  const isSupervisor = normalizedRole === "supervisor";
  const primaryNavigation = isSupervisor
    ? [
        globalPortalNavigation[0],
        ...supervisorPortalNavigation,
        ...globalPortalNavigation.slice(1),
      ]
    : globalPortalNavigation;

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const closeFromOutside = (event: MouseEvent) => {
      if (!navigationRef.current?.contains(event.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    const closeFromEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setMobileMenuOpen(false);
      mobileMenuButtonRef.current?.focus();
    };
    document.addEventListener("mousedown", closeFromOutside);
    document.addEventListener("keydown", closeFromEscape);
    return () => {
      document.removeEventListener("mousedown", closeFromOutside);
      document.removeEventListener("keydown", closeFromEscape);
    };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <aside
      ref={navigationRef}
      className="cp-global-nav"
      aria-label="Clinician Portal"
      data-mobile-open={mobileMenuOpen || undefined}
    >
      <div className="cp-global-nav__bar">
        <PortalLink
          className="cp-brand"
          path="/clinician-portal"
          clinicianCode={clinicianCode}
          aria-label="Education For All Clinician Portal"
          onClick={closeMobileMenu}
        >
          <img src="/EduForAll.png" alt="Education For All" />
          <span>Clinician Portal</span>
        </PortalLink>
        <button
          ref={mobileMenuButtonRef}
          type="button"
          className="cp-global-nav__toggle"
          aria-expanded={mobileMenuOpen}
          aria-controls={mobileMenuId}
          aria-label={
            mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"
          }
          onClick={() => setMobileMenuOpen((open) => !open)}
        >
          {mobileMenuOpen ? (
            <X aria-hidden="true" />
          ) : (
            <Menu aria-hidden="true" />
          )}
          <span>{mobileMenuOpen ? "Close" : "Menu"}</span>
        </button>
      </div>
      <nav id={mobileMenuId} className="cp-global-nav__items">
        {primaryNavigation.map((item) => (
          <PortalLink
            key={item.key}
            className="cp-global-nav__link"
            data-active={activeKey === item.key || undefined}
            aria-current={activeKey === item.key ? "page" : undefined}
            path={item.path}
            clinicianCode={clinicianCode}
            onClick={closeMobileMenu}
          >
            <GlobalNavigationIcon navigationKey={item.key} />
            <span>{item.label}</span>
          </PortalLink>
        ))}
        <div className="cp-global-nav__section">
          <p>Reports</p>
          {insightsPortalNavigation.map((item) => (
            <PortalLink
              key={item.key}
              className="cp-global-nav__link"
              data-active={activeKey === item.key || undefined}
              aria-current={activeKey === item.key ? "page" : undefined}
              path={item.path}
              clinicianCode={clinicianCode}
              onClick={closeMobileMenu}
            >
              <GlobalNavigationIcon navigationKey={item.key} />
              <span>{item.label}</span>
            </PortalLink>
          ))}
        </div>
        {isAdmin ? (
          <div className="cp-global-nav__section">
            <p>Administration</p>
            {adminPortalNavigation.map((item) => (
              <PortalLink
                key={item.key}
                className="cp-global-nav__link"
                data-active={activeKey === item.key || undefined}
                aria-current={activeKey === item.key ? "page" : undefined}
                path={item.path}
                clinicianCode={clinicianCode}
                onClick={closeMobileMenu}
              >
                <GlobalNavigationIcon navigationKey={item.key} />
                <span>{item.label}</span>
              </PortalLink>
            ))}
          </div>
        ) : null}
      </nav>
    </aside>
  );
}

interface ClientPrimaryTabsProps {
  patientId: string;
  clinicianCode: string;
  activeKey: ClientPrimaryNavigationKey;
}

export function ClientPrimaryTabs({
  patientId,
  clinicianCode,
  activeKey,
}: ClientPrimaryTabsProps) {
  const workspace = useClientWorkspace();
  return (
    <>
      <nav className="cp-primary-tabs" aria-label="Client sections">
        {clientPrimaryNavigation.map((item) => (
          <PortalLink
            key={item.key}
            className="cp-primary-tabs__link"
            data-active={activeKey === item.key || undefined}
            aria-current={activeKey === item.key ? "page" : undefined}
            path={
              workspace.source === "team"
                ? withTeamClinicScope(
                    item.path(patientId, workspace.source),
                    readTeamClinicId(),
                  )
                : item.path(patientId, workspace.source)
            }
            clinicianCode={clinicianCode}
          >
            {item.label}
          </PortalLink>
        ))}
      </nav>
      <ClientWorkspaceContextIndicator clinicianCode={clinicianCode} />
    </>
  );
}

export function TeamTabs({
  clinicianCode,
  activeKey,
  clinicId = readTeamClinicId(),
}: {
  clinicianCode: string;
  activeKey: TeamNavigationKey;
  clinicId?: string;
}) {
  return (
    <nav className="cp-primary-tabs cp-team-tabs" aria-label="Team sections">
      {teamNavigation.map((item) => (
        <PortalLink
          key={item.key}
          className="cp-primary-tabs__link"
          data-active={activeKey === item.key || undefined}
          aria-current={activeKey === item.key ? "page" : undefined}
          path={withTeamClinicScope(item.path, clinicId)}
          clinicianCode={clinicianCode}
        >
          {item.label}
        </PortalLink>
      ))}
    </nav>
  );
}

interface SegmentedNavigationProps {
  items: readonly SecondaryNavigationItem[];
  patientId: string;
  clinicianCode: string;
  activeKey?: ClientSecondaryNavigationKey;
}

export function SegmentedNavigation({
  items,
  patientId,
  clinicianCode,
  activeKey,
}: SegmentedNavigationProps) {
  const workspace = useClientWorkspace();
  return (
    <nav className="cp-segmented-nav" aria-label="Page sections">
      {items.map((item) => (
        <PortalLink
          key={item.key}
          className="cp-segmented-nav__link"
          data-active={activeKey === item.key || undefined}
          aria-current={activeKey === item.key ? "page" : undefined}
          path={
            workspace.source === "team"
              ? withTeamClinicScope(
                  item.path(patientId, workspace.source),
                  readTeamClinicId(),
                )
              : item.path(patientId, workspace.source)
          }
          clinicianCode={clinicianCode}
        >
          {item.label}
        </PortalLink>
      ))}
    </nav>
  );
}
